import { useCallback, useState } from "react";
import { Button, Flex, IconButton, Switch, Text, TextArea, Tooltip } from "@radix-ui/themes";
import {
  CheckCircledIcon,
  CheckIcon,
  Cross2Icon,
  Pencil2Icon,
  StarIcon,
  TrashIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import Select from "@/components/shared/select";
import { FieldGroup } from "@/v2/components/fields/field-group/FieldGroup";
import { SelectOrCreate } from "@/v2/components/fields/select-or-create/SelectOrCreate";
import { CreateSampleFormQuestion } from "@/v2/entities/sampling/Sampling.api";
import { TicketScore } from "../qa-score/TicketScore";
import styles from "./QuestionItem.module.scss";

interface QuestionTypeIconProps {
  type: string;
}

function QuestionTypeIcon({ type }: QuestionTypeIconProps) {
  const iconStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    marginRight: "8px",
  };

  switch (type) {
    case "rating":
      return (
        <div
          style={{ ...iconStyles, backgroundColor: "var(--orange-3)" }}
        >
          <StarIcon style={{ color: "var(--orange-9)" }} width={16} height={16} />
        </div>
      );
    case "binary":
      return (
        <div
          style={{ ...iconStyles, backgroundColor: "var(--green-3)" }}
        >
          <CheckCircledIcon
            style={{ color: "var(--green-9)" }}
            width={16}
            height={16}
          />
        </div>
      );
    default:
      return null;
  }
}

interface QuestionError {
  content?: string;
  title?: string;
  type?: string;
}

function validateQuestion(
  question: CreateSampleFormQuestion
): QuestionError | undefined {
  const errors: QuestionError = {};

  if (question.content == null || question.content === "") {
    errors.content = "Evaluation question cannot be empty";
  }

  if (question.title == null || question.title === "") {
    errors.title = "Category cannot be empty";
  }

  if (question.type == null) {
    errors.type = "Type cannot be empty";
  }

  return Object.keys(errors).length > 0 ? errors : undefined;
}

interface QuestionItemProps {
  value: CreateSampleFormQuestion;
  isEditing: boolean;
  allTitles: string[];
  viewOnly?: boolean;
  onSave?: (key: string, question: CreateSampleFormQuestion) => void;
  onDiscard?: () => void;
  onDelete?: () => void;
  onStartEditing?: () => void;
}

export function QuestionItem({
  value,
  isEditing,
  allTitles,
  viewOnly = false,
  onSave,
  onDiscard,
  onDelete,
  onStartEditing,
}: QuestionItemProps) {
  const [innerValue, setInnerValue] = useState<CreateSampleFormQuestion>(value);
  const [errors, setErrors] = useState<QuestionError | undefined>(undefined);
  const [showBriefInput, setShowBriefInput] = useState(!!value.brief);

  const handleSave = useCallback(() => {
    const errors = validateQuestion(innerValue);
    if (errors != null) {
      setErrors(errors);
      return;
    }

    onSave?.(innerValue.key, innerValue);
    setShowBriefInput(false);
  }, [onSave, innerValue]);

  const handleDiscard = useCallback(() => {
    setInnerValue(value);
    setShowBriefInput(!!value.brief);
    onDiscard?.();
  }, [value, onDiscard]);

  if (!isEditing || viewOnly) {
    return (
      <div
        key={value.key}
        style={{
          padding: "16px",
          border: "1px solid var(--gray-4)",
          borderRadius: "8px",
          boxShadow: "var(--shadow-1)",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          backgroundColor: "var(--backgroundDefault)"
        }}
      >
        <Flex gap={"2"} justify={"between"}>
          <TicketScore
            viewOnly
            id={1}
            title={value.title}
            content={value.content}
            autoScore={value.type === "rating" ? 3 : 1}
            adjustedScore={value.type === "rating" ? 3 : 1}
            reasonsForNegativeScore={[]}
            scoreEntity="ticket"
            type={value.type}
            onUpdateTicketScore={() => Promise.resolve()}
            brief={value.brief}
          />

          {!viewOnly && (
            <Flex gap={"4"}>
              <IconButton
                variant="ghost"
                color="gray"
                size="2"
                highContrast
                onClick={onStartEditing}
              >
                <Pencil2Icon />
              </IconButton>

              <IconButton
                variant="ghost"
                color="red"
                size="2"
                highContrast
                onClick={onDelete}
              >
                <TrashIcon />
              </IconButton>
            </Flex>
          )}
        </Flex>
      </div>
    );
  }

  return (
    <div
      key={value.key}
      style={{
        padding: "16px",
        border: "1px solid var(--gray-4)",
        borderRadius: "8px",
        boxShadow: "var(--shadow-1)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        backgroundColor: "var(--backgroundDefault)"
      }}
    >
      <FieldGroup
        label="Evaluation Question"
        classes={{
          wrapper: styles.fieldGroupWrapper,
          root: styles.fieldGroupRoot,
        }}
        error={errors?.content}
      >
        <TextArea
          value={innerValue.content}
          onChange={(e) =>
            setInnerValue({ ...innerValue, content: e.target.value })
          }
          className="w-full"
          placeholder={
            "Example: Did the agent provide a clear and accurate solution to the customer's problem?"
          }
        />
      </FieldGroup>

      <FieldGroup
        label="Category"
        classes={{
          wrapper: styles.fieldGroupWrapper,
          root: styles.fieldGroupRoot,
        }}
        error={errors?.title}
      >
        <SelectOrCreate
          value={value.title}
          onChange={(newTitle) =>
            setInnerValue({ ...innerValue, title: newTitle })
          }
          options={allTitles}
          hint={`Group similar questions together to calculate average scores. Example categories: "Problem Resolution", "Communication", "Technical Accuracy"`}
        />
      </FieldGroup>

      <FieldGroup
        label="Type"
        classes={{
          wrapper: styles.fieldGroupWrapper,
          root: styles.fieldGroupRoot,
        }}
        error={errors?.type}
      >
        <QuestionTypeIcon type={innerValue.type} />

        <Select
          value={innerValue.type}
          onChange={(newType) => {
            if (newType !== "rating" && newType !== "binary") {
              return;
            }

            setInnerValue({
              ...innerValue,
              type: newType,
            });
          }}
          options={[
            { label: "Rating (1-5)", value: "rating" },
            { label: "Achieved / Not Achieved", value: "binary" },
          ]}
        />
      </FieldGroup>

      <FieldGroup
        label="Include Sources"
        classes={{
          wrapper: styles.fieldGroupWrapper,
          root: styles.fieldGroupRoot,
        }}
      >
        <Flex align="center" gap="2">
          <Switch
            checked={innerValue.includeSources ?? false}
            onCheckedChange={(checked) =>
              setInnerValue({ ...innerValue, includeSources: checked })
            }
          />
          <Text size="2" color="gray">
            Include source references in the evaluation
          </Text>
        </Flex>
      </FieldGroup>

      <FieldGroup
        label="Brief Instructions"
        classes={{
          wrapper: styles.fieldGroupWrapper,
          root: styles.fieldGroupRoot,
        }}
      >
        {!showBriefInput && !innerValue.brief ? (
          <Tooltip content="Add specific instructions for evaluating this question">
            <Button
              variant="outline"
              color="gray"
              size="2"
              onClick={() => setShowBriefInput(true)}
              className="w-full justify-center"
            >
              <PlusIcon />
              Add Brief
            </Button>
          </Tooltip>
        ) : (
          <TextArea
            value={innerValue.brief || ""}
            onChange={(e) =>
              setInnerValue({ ...innerValue, brief: e.target.value })
            }
            className="w-full"
            placeholder="Provide specific instructions for evaluating this question..."
            rows={4}
            style={{ resize: "vertical" }}
          />
        )}
      </FieldGroup>

      <Flex justify={"end"} gap={"2"}>
        <Button
          variant="soft"
          color="gray"
          size="2"
          highContrast
          onClick={handleDiscard}
        >
          <Cross2Icon />
          Discard
        </Button>

        <Button
          variant="solid"
          color="gray"
          size="2"
          highContrast
          onClick={handleSave}
        >
          <CheckIcon />
          Save
        </Button>
      </Flex>
    </div>
  );
}
